import { Clock, CheckCircle, DollarSign, Home, ThumbsUp, ShieldCheck, ScanLine, Calendar, Users, Ruler, CalendarClock, Hammer } from 'lucide-react';

import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/complete-home.js';



export default function completeHomeRemodeling() {

  const service = {
    title: 'Complete Home Remodeling',
    image: '/hero/home-remodeling-services-in-los-angeles.webp',
    description: 'Transform your home with Modern Build. Full design-build remodeling for every room. Free estimate: (323) 300-4130',
    link: '/services/complete-home-remodeling',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));

    // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "Complete Home Remodeling Services", "item": pageUrl }
    ]
  };

  
  return (
    <div className="bg-white">
    <>
      <Head>
        <title>Complete Home Remodeling Experts | Free Estimate | Modern Build</title>
        <meta name="title" content="Complete Home Remodeling Experts | Free Estimate | Modern Build" />
        <meta name="description" content="Transform your home with Modern Build. Full design-build remodeling for every room. Free estimate: (323) 300-4130" />
        <meta name="keywords" content="home remodeling, complete home renovation, full house remodel, los angeles home remodeling, california home renovation, modern build" />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/complete-home-remodeling" />

        <meta property="og:title" content="Complete Home Remodeling Experts | Free Estimate | Modern Build" />
        <meta property="og:description" content="Transform your home with Modern Build. Full design-build remodeling for every room. Free estimate: (323) 300-4130" />
        <meta property="og:image" content="https://modernbuildremodel.com/hero/home-remodeling-services-in-los-angeles.webp" />
        <meta property="og:url" content="https://modernbuildremodel.com/services/complete-home-remodeling" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Complete Home Remodeling Experts | Free Estimate | Modern Build" />
        <meta name="twitter:description" content="Transform your home with Modern Build. Full design-build remodeling for every room. Free estimate: (323) 300-4130" />
        <meta name="twitter:image" content="https://modernbuildremodel.com/hero/home-remodeling-services-in-los-angeles.webp" />
        <meta name="twitter:site" content="@modernbuildhome" />


        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Complete Home Remodeling Services | Free Estimate | Modern Build',
                description: 'Luxury full home remodeling services to redesign, rebuild, and transform your living space. Modern Build delivers seamless and stress-free renovations.',
                slug: '/services/complete-home-remodeling'
              })
            )
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>
      <Header />
      <Hero category="completeHome" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="COMPLETE HOME REMODELING SERVICES"
        title="Design. Build. Transform Your Entire Home."
        background="white"
        description="Modern Build specializes in full-service home remodeling that reinvents your space for modern living. Whether you're looking to update a few rooms or fully reimagine your home’s layout, our team brings creativity, precision, and craftsmanship to every detail."
        extraText="From architecture and design to construction and finishes, we manage every aspect of your home remodel to deliver beautiful, high-quality results — built just for you."
        phone="(323) 300-4130"
        image1="/split/complete-home-remodeling-services-in-los-angeles-ca.webp"
        image2="/split/best-complete-home-remodeling-in-los-angeles-ca.webp"
      />

      <Benefits
        title="What’s Included In Your Complete Home Remodel?"
        subtitle="We handle everything from concept to completion."
        items={[
          {
            icon: <Home className="w-8 h-8 text-primary" />,
            title: "Full Home Design & Planning",
          },
          {
            icon: <ScanLine className="w-8 h-8 text-primary" />,
            title: "Kitchen, Bath, and Living Room Renovations",
            href: "/services/kitchen-remodeling",
          },
          {
            icon: <Clock className="w-8 h-8 text-primary" />,
            title: "Smart Home Upgrades & Energy Efficiency",
            href: "/services/energy-efficient-windows",
          },
          {
            icon: <ShieldCheck className="w-8 h-8 text-primary" />,
            title: "Structural Changes & Additions",
            href: "/services/adu",
          },
          {
            icon: <Calendar className="w-8 h-8 text-primary" />,
            title: "Clear Timelines & Project Management",
          },
          {
            icon: <CheckCircle className="w-8 h-8 text-primary" />,
            title: "Permits, Inspections & City Approvals",
          },
          {
            icon: <ThumbsUp className="w-8 h-8 text-primary" />,
            title: "Custom Design Consultations",
          },
          {
            icon: <DollarSign className="w-8 h-8 text-primary" />,
            title: "Flexible Financing Available",
            href: "/financing",
          },
        ]}
      />

      <ContentBlock
        subtitle="FULL-SERVICE HOME REMODELING"
        title="Reimagine Every Room In Your Home"
        description="Modern Build offers complete solutions for transforming your living space — from kitchen and bathroom remodeling to adding new bedrooms, reconfiguring layouts, and refreshing interiors with modern finishes. Whatever your dream, we can bring it to life."
        extraText="Our team ensures every project is personalized, meticulously planned, and completed to the highest standards — with a stress-free experience from start to finish."
        buttonText="Get a Free Estimate"
        background="white"
      />

      <ProcessRoadmap
        title="Our Process"
        subtitle="Your dream home, delivered step-by-step"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "Initial Consultation",
            description:
              "We meet with you to understand your goals, preferences, and vision for your home's transformation.",
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Design Development",
            description:
              "Our architects and designers collaborate with you to create a customized plan tailored to your lifestyle and style.",
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Planning, Permits, & Timeline",
            description:
              "We manage all the details — from securing permits to establishing a clear and realistic project timeline.",
          },
          {
            icon: <Hammer className="w-10 h-10 text-primary" />,
            title: "Build & Final Reveal",
            description:
              "Our experienced construction team brings your dream home to life, ensuring everything is completed perfectly before your final walkthrough.",
          },
        ]}
      />

      <Split
        subtitle="TAILORED DESIGN EXPERIENCE"
        title="Transform Your Home With Confidence"
        background="white"
        description="Modern Build’s personalized remodeling approach means your new home will be designed exactly to your needs, style, and dreams. With detailed planning, quality craftsmanship, and transparent communication, we turn your vision into reality."
        extraText="We use advanced tools like 3D renderings and material boards so you can visualize your project before construction begins — eliminating surprises."
        phone="(323) 300-4130"
        image1="/split/best-home-remodeling-in-los-angeles-ca.webp"
        image2="/split/complete-living-room-remodeling-services-in-los-angeles-ca.webp"
        reverse={true}
      />

      <CTASection
        title="Remodel Now. Pay Later."
        subtitle="100% Financing Available with $0 Down*"
        buttonText="Get Pre-Approved Today"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />

      <FAQ category="complete-home" />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}
